import { SagaIterator } from 'redux-saga'
import * as effects from 'redux-saga/effects'
import * as api from './api'
import * as actions from './actions'
import { formatErrorMessage } from '../../utils/helpers'
import * as Sentry from '@sentry/react'
import { PayloadCreateDataType, PayloadEditDataType } from './types'

function* fetchParents(): SagaIterator {
  try {
    const parents = yield effects.call(api.fetchParents)
    yield effects.put(actions.fetchParentsSuccess(parents.data))
  } catch (error) {
    console.warn('Cannot fetch parents', error)
    yield effects.put(actions.fetchParentsSuccess(formatErrorMessage(error)))
    Sentry.captureException(error)
  }
}

function* createParent(payload: PayloadCreateDataType): SagaIterator {
  try {
    const {
      gender,
      first_name,
      middle_name,
      last_name,
      birthdate,
      contact_email,
      contact_phone,
    } = payload
    yield effects.call(
      api.createParent,
      gender,
      first_name,
      middle_name,
      last_name,
      birthdate,
      contact_email,
      contact_phone
    )
    yield effects.put(actions.createParentSuccess())
    yield effects.put(actions.fetchParents())
  } catch (error) {
    console.warn('Cannot create parent', error)
    yield effects.put(actions.createParentFailure(formatErrorMessage(error)))
    Sentry.captureException(error)
  }
}

function* editParent(payload: PayloadEditDataType): SagaIterator {
  try {
    const {
      parent_id,
      gender,
      first_name,
      middle_name,
      last_name,
      birthdate,
      contact_email,
      contact_phone,
    } = payload
    yield effects.call(
      api.editParent,
      parent_id,
      gender,
      first_name,
      middle_name,
      last_name,
      birthdate,
      contact_email,
      contact_phone
    )
    yield effects.put(actions.editParentSuccess())
    yield effects.put(actions.fetchParents())
  } catch (error) {
    console.warn('Cannot edit parent', error)
    yield effects.put(actions.editParentFailure(formatErrorMessage(error)))
    Sentry.captureException(error)
  }
}

function* uploadPhoto(payload: {
  parent_id: string
  photo: File
}): SagaIterator {
  try {
    const { parent_id, photo } = payload
    yield effects.call(api.uploadPhoto, parent_id, photo)
    yield effects.put(actions.uploadPhotoSuccess())
    yield effects.put(actions.fetchParents())
  } catch (error) {
    console.warn('Cannot upload parent photo', error)
    yield effects.put(actions.uploadPhotoFailure(formatErrorMessage(error)))
    Sentry.captureException(error)
  }
}

function* removePhoto(payload: { parent_id: string }): SagaIterator {
  try {
    yield effects.call(api.removePhoto, payload.parent_id)
    yield effects.put(actions.removePhotoSuccess())
    yield effects.put(actions.fetchParents())
  } catch (error) {
    console.warn('Cannot remove parent photo', error)
    yield effects.put(actions.removePhotoFailure(formatErrorMessage(error)))
    Sentry.captureException(error)
  }
}

function* watchFetchParents(): SagaIterator {
  yield effects.takeEvery(actions.fetchParents.toString(), fetchParents)
}

function* watchCreateParent(): SagaIterator {
  yield effects.takeEvery(actions.createParent.toString(), createParent)
}

function* watchEditParent(): SagaIterator {
  yield effects.takeEvery(actions.editParent.toString(), editParent)
}

function* watchUploadPhoto(): SagaIterator {
  yield effects.takeEvery(actions.uploadPhoto.toString(), uploadPhoto)
}

function* watchRemovePhoto(): SagaIterator {
  yield effects.takeEvery(actions.removePhoto.toString(), removePhoto)
}

export default function* watchProfileParents(): SagaIterator {
  yield effects.all([
    effects.fork(watchFetchParents),
    effects.fork(watchCreateParent),
    effects.fork(watchEditParent),
    effects.fork(watchUploadPhoto),
    effects.fork(watchRemovePhoto),
  ])
}
